import logo from '../../assets/logo.png';
import { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { resetPasswordToken } from '../../api/identity';
import * as Yup from 'yup';

interface ErrorState {
  [key: string]: string | undefined; // Adding index signature
  password?: string;
  repeatPassword?: string;
}

const ResetPassword = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  const [currentStep, setCurrentStep] = useState(1);

  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [errors, setErrors] = useState<ErrorState>({});

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const togglePasswordVisibility = () =>
    setIsPasswordVisible(!isPasswordVisible);

  const [isRepeatPasswordVisible, setIsRepeatPasswordVisible] = useState(false);
  const toggleRepeatPasswordVisibility = () =>
    setIsRepeatPasswordVisible(!isRepeatPasswordVisible);

  const navigate = useNavigate();

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(/(?=.*[0-9])/, 'Password must contain at least one number')
      .matches(/(?=.*[a-zA-Z])/, 'Password must contain at least one letter')
      .required('Password is required'),
    repeatPassword: Yup.string()
      .required('Please retype your password')
      .oneOf([Yup.ref('password')], "These passwords don't match up"),
  });

  const validate = async () => {
    try {
      await validationSchema.validate(
        { password, repeatPassword },
        { abortEarly: false }
      );
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: ErrorState = {};
        err.inner.forEach((error: Yup.ValidationError) => {
          if (typeof error.path === 'string') {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
        return false;
      }
      console.error('An unexpected error occurred:', err);
      return false;
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const isValid = await validate();
    if (isValid && token) {
      try {
        await resetPasswordToken(token, repeatPassword, password);
        navigate('/login');
      } catch (error) {
        console.log('Failed to reset password', error);
      }
    }
  };

  const passwordTooSmall = password.length < 8 || repeatPassword.length < 8;
  const passwordsDontMatch = password !== repeatPassword;

  switch (currentStep) {
    case 1:
      return (
        <div className="flex flex-col items-center justify-center h-screen relative">
          <div className="flex flex-col items-center bg-grey rounded-3xl p-8 w-[370px] md:w-[500px] md:m-4">
            <img src={logo} className="h-8 mb-8" />
            <h1 className="text-xl">Let's reset your password</h1>
            <h2 className="text-sm mt-6">Please enter your new password</h2>
            <form onSubmit={handleSubmit}>
              <div className="flex flex-row items-center bg-white bg-opacity-5 rounded-3xl mt-6">
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-grow bg-white bg-opacity-0 rounded-3xl px-4 py-2"
                  placeholder="Password"
                  type={isPasswordVisible ? 'text' : 'password'}
                />
                {isPasswordVisible ? (
                  <EyeIcon
                    className="w-6 h-6 mx-4 cursor-pointer"
                    onClick={togglePasswordVisibility}
                  />
                ) : (
                  <EyeSlashIcon
                    className="w-6 h-6 mx-4 cursor-pointer"
                    onClick={togglePasswordVisibility}
                  />
                )}
              </div>
              {errors.password && (
                <div className="text-xs text-red mt-2 text-center">
                  {errors.password}
                </div>
              )}

              <div className="flex flex-row items-center bg-white bg-opacity-5 rounded-3xl mb-4 mt-4">
                <input
                  value={repeatPassword}
                  onChange={(e) => setRepeatPassword(e.target.value)}
                  className="flex-grow bg-white bg-opacity-0 rounded-3xl px-4 py-2"
                  placeholder="Repeat Password"
                  type={isRepeatPasswordVisible ? 'text' : 'password'}
                />
                {isRepeatPasswordVisible ? (
                  <EyeIcon
                    className="w-6 h-6 mx-4 cursor-pointer"
                    onClick={toggleRepeatPasswordVisibility}
                  />
                ) : (
                  <EyeSlashIcon
                    className="w-6 h-6 mx-4 cursor-pointer"
                    onClick={toggleRepeatPasswordVisibility}
                  />
                )}
              </div>
              {errors.repeatPassword && (
                <div className="text-xs text-red mt-2">
                  {errors.repeatPassword}
                </div>
              )}
              <div className="text-xs text-red flex flex-row justify-center mt-2">
                {passwordTooSmall
                  ? 'Password must be at least 8 characters,'
                  : ''}
              </div>
              <div className="text-xs text-red flex flex-row justify-center">
                {passwordsDontMatch ? 'Passwords must match' : ''}
              </div>
              <div className="flex flex-row justify-center">
                <button
                  className={`rounded-3xl text-black px-4 py-2 mb-4 mt-4 flex-grow hover:brightness-90
                ${
                  passwordTooSmall || passwordsDontMatch
                    ? 'bg-white'
                    : 'bg-yellow'
                }`}
                  type="submit"
                  disabled={passwordTooSmall || passwordsDontMatch}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      );
    case 2:
      return (
        <div className="flex flex-col items-center justify-center h-screen relative">
          <div className="flex flex-col items-center bg-grey rounded-3xl p-8 w-[370px] md:w-[500px] md:m-4">
            <img src={logo} className="h-8 mb-8" />
            <h1 className="text-xl">Password reset complete</h1>
            <button
              className="rounded-3xl text-black px-4 py-2 mb-4 mt-4 flex-grow bg-yellow w-96"
              type="button"
              onClick={() => navigate('/login')}
            >
              Login
            </button>
          </div>
        </div>
      );
  }
};

export default ResetPassword;
